import { Component, h, render, eventBus } from '@monygroupcorp/microact';
import WalletModal from '../Wallet/WalletModal.js';
import { SettingsModal } from '../SettingsModal/SettingsModal.js';
import { ethers } from 'ethers';

/**
 * FloatingWalletButton component
 * Persistent floating wallet button (bottom-right corner) with power user dropdown menu
 * Replaces WalletSplash for non-blocking wallet connection
 */
export class FloatingWalletButton extends Component {
    constructor(props) {
        super(props);
        this.walletService = props.walletService;
        this.walletModal = null;
        this.settingsModal = null;
        this.state = {
            walletConnected: false,
            address: null,
            balance: '0.00',
            loading: true,
            menuOpen: false,
            // Conditional menu items
            hasExecTokens: false,
            isVaultBenefactor: false
        };
    }

    didMount() {
        console.log('[FloatingWalletButton] didMount called');
        this.initialize();
        this.setupClickOutsideHandler();
    }

    async initialize() {
        await this.checkWalletConnection();
        this.setupEventListeners();
    }

    async checkWalletConnection() {
        console.log('[FloatingWalletButton] checkWalletConnection called');
        try {
            // Initialize wallet service if needed (handles EIP-6963 + legacy discovery)
            if (!this.walletService.isInitialized) {
                await this.walletService.initialize();
            }

            // Check if wallet service thinks it's connected
            let isConnected = this.walletService.isConnected();
            let address = this.walletService.getAddress();

            // If not connected, check if window.ethereum already has connected accounts
            if (!isConnected && typeof window.ethereum !== 'undefined') {
                try {
                    const accounts = await window.ethereum.request({ method: 'eth_accounts' });
                    const hasAccounts = accounts && accounts.length > 0;

                    if (hasAccounts) {
                        const walletType = this.walletService._detectWalletType();
                        console.log('[FloatingWalletButton] Detected existing connection:', walletType, accounts[0]);

                        try {
                            await this.walletService.selectWallet(walletType);
                            await this.walletService.connect();
                            isConnected = this.walletService.isConnected();
                            address = this.walletService.getAddress();

                            if (isConnected) {
                                console.log('[FloatingWalletButton] Auto-connected to existing session:', walletType);
                            }
                        } catch (connectError) {
                            console.log('[FloatingWalletButton] Could not auto-connect:', connectError.message);
                        }
                    }
                } catch (error) {
                    console.log('[FloatingWalletButton] Could not check existing accounts:', error);
                }
            }

            if (isConnected && address) {
                await this.loadWalletInfo(address);
            } else {
                this.setState({ loading: false, walletConnected: false });
            }
        } catch (error) {
            console.error('[FloatingWalletButton] Error checking wallet connection:', error);
            this.setState({ loading: false, walletConnected: false });
        }
    }

    /**
     * Load wallet info (balance, EXEC tokens, vault benefactor status)
     */
    async loadWalletInfo(address) {
        try {
            // Get ETH balance
            let provider = this.walletService.ethersProvider;
            if (!provider && typeof window.ethereum !== 'undefined') {
                provider = new ethers.providers.Web3Provider(window.ethereum);
            }

            let balance = '0.00';
            if (provider && address) {
                const balanceWei = await provider.getBalance(address);
                balance = parseFloat(ethers.utils.formatEther(balanceWei)).toFixed(4);
            }

            // TODO: Check EXEC token holdings for governance menu visibility
            // const hasExecTokens = await this.checkExecTokens(address);
            const hasExecTokens = false; // Placeholder for now

            // TODO: Check vault benefactor status
            // const isVaultBenefactor = await this.checkVaultBenefactor(address);
            const isVaultBenefactor = false; // Placeholder for now

            this.setState({
                walletConnected: true,
                address,
                balance,
                loading: false,
                hasExecTokens,
                isVaultBenefactor
            });
        } catch (error) {
            console.error('[FloatingWalletButton] Failed to load wallet info:', error);
            this.setState({
                walletConnected: true,
                address,
                loading: false
            });
        }
    }

    /**
     * TODO: Check if user holds EXEC tokens
     */
    async checkExecTokens(address) {
        // Will implement when ERC404 adapter is wired up
        // const execBalance = await ERC404Adapter.balanceOf(address, EXEC_TOKEN_ADDRESS);
        // return execBalance > 0;
        return false;
    }

    /**
     * TODO: Check if user is a vault benefactor
     */
    async checkVaultBenefactor(address) {
        // Will implement when vault adapters are wired up
        // Iterate vaults, check getBenefactorShares(address) > 0
        return false;
    }

    setupEventListeners() {
        // Listen for wallet connection
        this.subscribe('wallet:connected', async (data) => {
            await this.loadWalletInfo(data.address);
        });

        // Listen for wallet disconnection
        this.subscribe('wallet:disconnected', () => {
            this.setState({
                walletConnected: false,
                address: null,
                balance: '0.00',
                menuOpen: false,
                hasExecTokens: false,
                isVaultBenefactor: false
            });
        });

        // Listen for wallet/account change
        this.subscribe('wallet:changed', async (data) => {
            await this.loadWalletInfo(data.address);
        });

        // Listen for late EIP-6963 provider announcements
        this.subscribe('wallet:providerdiscovered', () => {
            // Invalidate cached modal so next open picks up new providers
            if (this.walletModal) {
                this.walletModal = null;
            }
        });
    }

    setupClickOutsideHandler() {
        const handleClickOutside = (e) => {
            if (!this.element) return;

            // Check if click is outside the floating wallet button
            if (!this.element.contains(e.target) && this.state.menuOpen) {
                this.setState({ menuOpen: false });
            }
        };

        document.addEventListener('click', handleClickOutside);

        this.registerCleanup(() => {
            document.removeEventListener('click', handleClickOutside);
        });
    }

    handleButtonClick(e) {
        e.preventDefault();
        e.stopPropagation();

        if (this.state.walletConnected) {
            // Toggle dropdown menu
            this.setState({ menuOpen: !this.state.menuOpen });
        } else {
            // Show wallet connection modal
            this.showWalletModal();
        }
    }

    showWalletModal() {
        const wallets = this.walletService
            ? this.walletService.getAvailableWallets()
            : {};

        if (!this.walletModal) {
            if (!this.modalRoot) {
                this.modalRoot = document.createElement('div');
                document.body.appendChild(this.modalRoot);
            }
            render(h(WalletModal, {
                wallets,
                onWalletSelected: async (walletKey) => {
                    await this.handleWalletSelection(walletKey);
                },
                ref: instance => this.walletModal = instance
            }), this.modalRoot);
        } else {
            // Update wallets for late-arriving EIP-6963 providers
            this.walletModal.props.wallets = wallets;
        }

        this.walletModal?.show();
    }

    async handleWalletSelection(walletKey) {
        try {
            // Select the wallet (handles EIP-6963 and legacy)
            await this.walletService.selectWallet(walletKey);

            // Connect to the wallet
            await this.walletService.connect();

            // Store rdns-aware selectedWallet for future auto-reconnect
            if (this.walletService.selectedWallet) {
                localStorage.setItem('ms2fun_lastWallet', this.walletService.selectedWallet);
            }

        } catch (error) {
            console.error('[FloatingWalletButton] Error connecting wallet:', error);
        }
    }

    handleMenuItemClick(route) {
        // Close menu
        this.setState({ menuOpen: false });

        // Navigate to route
        if (window.router) {
            window.router.navigate(route);
        } else {
            window.location.href = route;
        }
    }

    handleDisconnect(e) {
        e.preventDefault();
        e.stopPropagation();

        try {
            this.walletService.disconnect();
            this.setState({ menuOpen: false });
        } catch (error) {
            console.error('[FloatingWalletButton] Failed to disconnect:', error);
        }
    }

  static get styles() {
    return `
    /* FloatingWalletButton - Bottom-right floating wallet connection button with power user dropdown */

    .floating-wallet-button {
        position: fixed;
        bottom: 2rem;
        right: 2rem;
        z-index: 9999; /* Above all content */
    }

    .floating-wallet-button .wallet-btn {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1.25rem;
        background: linear-gradient(135deg, rgba(139, 69, 19, 0.9), rgba(101, 67, 33, 0.9));
        border: 2px solid rgba(218, 165, 32, 0.6);
        border-radius: 50px;
        color: #fff;
        font-family: 'Cinzel', serif;
        font-size: 0.9rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(10px);
    }

    .floating-wallet-button .wallet-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(218, 165, 32, 0.4);
        border-color: rgba(218, 165, 32, 0.8);
        background: linear-gradient(135deg, rgba(160, 82, 45, 0.95), rgba(120, 80, 50, 0.95));
    }

    .floating-wallet-button .wallet-btn:active {
        transform: translateY(0);
    }

    /* Wallet icon */
    .floating-wallet-button .wallet-icon {
        font-size: 1.2rem;
        line-height: 1;
    }

    /* Wallet text/address */
    .floating-wallet-button .wallet-text,
    .floating-wallet-button .wallet-address {
        font-size: 0.875rem;
        white-space: nowrap;
    }

    /* Loading state */
    .floating-wallet-button.loading {
        pointer-events: none;
    }

    .floating-wallet-button .wallet-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid rgba(218, 165, 32, 0.3);
        border-top-color: rgba(218, 165, 32, 0.9);
        border-radius: 50%;
        animation: wallet-spin 0.8s linear infinite;
    }

    @keyframes wallet-spin {
        to { transform: rotate(360deg); }
    }

    /* Dropdown menu */
    .wallet-dropdown-menu {
        position: absolute;
        bottom: calc(100% + 0.5rem);
        right: 0;
        min-width: 240px;
        background: linear-gradient(135deg, rgba(139, 69, 19, 0.98), rgba(101, 67, 33, 0.98));
        border: 2px solid rgba(218, 165, 32, 0.6);
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(15px);
        overflow: hidden;
        animation: dropdown-slide-up 0.2s ease-out;
    }

    @keyframes dropdown-slide-up {
        from {
            opacity: 0;
            transform: translateY(10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    /* Dropdown header */
    .wallet-dropdown-menu .dropdown-header {
        padding: 1rem;
        background: rgba(0, 0, 0, 0.3);
        border-bottom: 1px solid rgba(218, 165, 32, 0.3);
    }

    .wallet-dropdown-menu .dropdown-address {
        font-family: 'Cinzel', serif;
        font-size: 0.875rem;
        font-weight: 600;
        color: rgba(218, 165, 32, 0.95);
        margin-bottom: 0.25rem;
    }

    .wallet-dropdown-menu .dropdown-balance {
        font-family: 'Lato', sans-serif;
        font-size: 0.75rem;
        color: rgba(255, 255, 255, 0.7);
    }

    /* Dropdown divider */
    .wallet-dropdown-menu .dropdown-divider {
        height: 1px;
        background: linear-gradient(90deg,
            transparent,
            rgba(218, 165, 32, 0.3) 50%,
            transparent
        );
        margin: 0;
    }

    /* Dropdown items */
    .wallet-dropdown-menu .dropdown-items {
        padding: 0.5rem;
    }

    .wallet-dropdown-menu .dropdown-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        width: 100%;
        padding: 0.75rem 1rem;
        background: transparent;
        border: none;
        border-radius: 8px;
        color: rgba(255, 255, 255, 0.9);
        font-family: 'Lato', sans-serif;
        font-size: 0.875rem;
        text-align: left;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .wallet-dropdown-menu .dropdown-item:hover {
        background: rgba(218, 165, 32, 0.15);
        color: #fff;
    }

    .wallet-dropdown-menu .dropdown-item:active {
        background: rgba(218, 165, 32, 0.25);
    }

    .wallet-dropdown-menu .dropdown-item .item-icon {
        font-size: 1.125rem;
        line-height: 1;
        opacity: 0.9;
    }

    .wallet-dropdown-menu .dropdown-item .item-label {
        flex: 1;
    }

    /* Disconnect button special styling */
    .wallet-dropdown-menu .dropdown-item.disconnect {
        color: rgba(255, 99, 71, 0.9);
    }

    .wallet-dropdown-menu .dropdown-item.disconnect:hover {
        background: rgba(255, 99, 71, 0.15);
        color: rgba(255, 99, 71, 1);
    }

    /* Responsive adjustments */
    @media (max-width: 768px) {
        .floating-wallet-button {
            bottom: 1rem;
            right: 1rem;
        }

        .floating-wallet-button .wallet-btn {
            padding: 0.625rem 1rem;
            font-size: 0.8rem;
        }

        .wallet-dropdown-menu {
            min-width: 200px;
            right: 0;
        }

        .wallet-dropdown-menu .dropdown-item {
            padding: 0.625rem 0.875rem;
            font-size: 0.8rem;
        }
    }

    /* Small screens - even more compact */
    @media (max-width: 480px) {
        .floating-wallet-button {
            bottom: 0.75rem;
            right: 0.75rem;
        }

        .floating-wallet-button .wallet-btn {
            padding: 0.5rem 0.875rem;
            font-size: 0.75rem;
        }

        .floating-wallet-button .wallet-icon {
            font-size: 1rem;
        }

        .wallet-dropdown-menu {
            min-width: 180px;
        }
    }

    /* Ensure menu appears above button when open */
    .floating-wallet-button.menu-open .wallet-btn {
        border-color: rgba(218, 165, 32, 0.9);
        background: linear-gradient(135deg, rgba(160, 82, 45, 0.95), rgba(120, 80, 50, 0.95));
    }
    `;
  }

    render() {
        console.log('[FloatingWalletButton] render called, loading:', this.state.loading, 'walletConnected:', this.state.walletConnected);
        if (this.state.loading) {
            return h('div', { className: 'floating-wallet-button loading' },
                h('div', { className: 'wallet-spinner' })
            );
        }

        const { walletConnected, address, balance, menuOpen, hasExecTokens, isVaultBenefactor } = this.state;

        if (!walletConnected) {
            // Not connected - show "Connect" button
            return h('div', { className: 'floating-wallet-button disconnected', ref: el => this.element = el },
                h('button', {
                    className: 'wallet-btn',
                    onClick: this.bind(this.handleButtonClick)
                },
                    h('span', { className: 'wallet-icon' }, '\uD83E\uDD8A'),
                    h('span', { className: 'wallet-text' }, 'Connect')
                )
            );
        }

        // Connected - show abbreviated address
        const truncatedAddress = `${address.slice(0, 6)}...${address.slice(-4)}`;

        return h('div', {
            className: `floating-wallet-button connected ${menuOpen ? 'menu-open' : ''}`,
            ref: el => this.element = el
        },
            h('button', {
                className: 'wallet-btn',
                title: `${this.escapeHtml(address)}\nBalance: ${balance} ETH`,
                onClick: this.bind(this.handleButtonClick)
            },
                h('span', { className: 'wallet-icon' }, '\uD83E\uDD8A'),
                h('span', { className: 'wallet-address' }, this.escapeHtml(truncatedAddress))
            ),
            menuOpen && this.renderDropdownMenu(address, balance, hasExecTokens, isVaultBenefactor)
        );
    }

    renderDropdownMenu(address, balance, hasExecTokens, isVaultBenefactor) {
        const truncatedAddress = `${address.slice(0, 6)}...${address.slice(-4)}`;

        return h('div', { className: 'wallet-dropdown-menu' },
            h('div', { className: 'dropdown-header' },
                h('div', { className: 'dropdown-address' }, this.escapeHtml(truncatedAddress)),
                h('div', { className: 'dropdown-balance' }, `${balance} ETH`)
            ),
            h('div', { className: 'dropdown-divider' }),
            h('div', { className: 'dropdown-items' },
                h('button', {
                    className: 'dropdown-item',
                    onClick: () => this.handleMenuItemClick('/portfolio')
                },
                    h('span', { className: 'item-icon' }, '\uD83D\uDCCA'),
                    h('span', { className: 'item-label' }, 'Portfolio')
                ),
                hasExecTokens && h('button', {
                    className: 'dropdown-item',
                    onClick: () => this.handleMenuItemClick('/governance')
                },
                    h('span', { className: 'item-icon' }, '\uD83D\uDDF3\uFE0F'),
                    h('span', { className: 'item-label' }, 'Governance')
                ),
                h('button', {
                    className: 'dropdown-item',
                    onClick: () => this.handleMenuItemClick('/staking')
                },
                    h('span', { className: 'item-icon' }, '\uD83C\uDFAF'),
                    h('span', { className: 'item-label' }, 'Staking')
                ),
                isVaultBenefactor && h('button', {
                    className: 'dropdown-item',
                    onClick: () => this.handleMenuItemClick('/portfolio?filter=vaults')
                },
                    h('span', { className: 'item-icon' }, '\uD83D\uDCB0'),
                    h('span', { className: 'item-label' }, 'Vault Positions')
                )
            ),
            h('div', { className: 'dropdown-divider' }),
            h('div', { className: 'dropdown-items' },
                h('button', {
                    className: 'dropdown-item',
                    onClick: () => {
                        this.setState({ menuOpen: false });
                        this.openSettings();
                    }
                },
                    h('span', { className: 'item-icon' }, '\u2699\uFE0F'),
                    h('span', { className: 'item-label' }, 'Settings')
                ),
                h('button', {
                    className: 'dropdown-item disconnect',
                    onClick: this.bind(this.handleDisconnect)
                },
                    h('span', { className: 'item-icon' }, '\uD83D\uDD0C'),
                    h('span', { className: 'item-label' }, 'Disconnect')
                )
            )
        );
    }

    openSettings() {
        if (!this.settingsModal) {
            this.settingsModal = new SettingsModal({ eventBus: eventBus });
        }
        this.settingsModal.show();
    }

    willUnmount() {
        // Clean up wallet modal
        if (this.walletModal) {
            this.walletModal.hide();
            this.walletModal = null;
        }

        // Clean up settings modal
        if (this.settingsModal) {
            this.settingsModal.hide();
            this.settingsModal = null;
        }
    }

    escapeHtml(text) {
        if (!text) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}
